import { Field, Form, FormProps } from "react-final-form";

import { ActivationData } from "@/api/endpoints/user";
import ArrowRight from "@/assets/icons/arrow-right.svg";
import {
  combineValidators,
  sequenceValidators,
  validateLength,
  validateMatches,
  validateRequired,
} from "@/utils";
import Button from "@components/Button";
import { FF_InputField, FF_PasswordField } from "@components/Field";

import styles from "./Activate.module.css";
import { i18n } from "@lingui/core";

type ActivateFormData = ActivationData & {
  fullname: string;
  password_verify: string;
};

const splitName = (name?: string) => {
  if (!name) {
    return {
      first_name: "",
      last_name: "",
    };
  }
  const [first, ...last] = name.split(/\s+/);
  return {
    first_name: first,
    last_name: last.join(" "),
  };
};

const prepareValues = (values: ActivateFormData): ActivationData => {
  return {
    activation_key: values.activation_key,
    password: values.password,
    ...splitName(values.fullname),
  };
};

export type ActivateProps = Pick<FormProps<ActivationData>, "initialValues"> & {
  onSubmit: (v: ActivationData) => void;
};

const ActivateForm: React.FunctionComponent<ActivateProps> = ({
  onSubmit,
  initialValues,
}) => (
  <Form<ActivateFormData>
    onSubmit={(v) => onSubmit(prepareValues(v))}
    initialValues={initialValues}
  >
    {({ handleSubmit, invalid, dirtySinceLastSubmit, hasValidationErrors }) => (
      <form onSubmit={handleSubmit} className={styles.formRoot}>
        <div className={styles.form}>
          {/**
           * Email is here for one reason. To display to the user that the name is not their username
           * and to prevent any confusion for password managers that might try to save the name as the username.
           *
           * - The email mustn't be editable.
           */}
          <Field
            block
            name="email"
            type="email"
            disabled
            label={L.ACTIVATE_EMAIL_LABEL}
            component={FF_InputField}
          />
          <Field
            block
            name="fullname"
            placeholder={L.ACTIVATE_NAME_PLACEHOLDER}
            label={L.ACTIVATE_NAME_LABEL}
            validate={combineValidators(
              validateRequired(i18n._(L.REQUIRED_FIELD_VALUE)),
              validateMatches(i18n._(L.ACTIVATE_LAST_NAME_MISSING), /\s/)
            )}
            type="text"
            component={FF_InputField}
            data-testid="activate-fullname"
          />
          <Field
            block
            name="password"
            autoComplete="new-password"
            placeholder={L.ACTIVATE_PASSWORD_PLACEHOLDER}
            label={L.ACTIVATE_PASSWORD_LABEL}
            validate={combineValidators(
              // Full logic is found in settings.py => AUTH_PASSWORD_VALIDATORS[]
              validateRequired(i18n._(L.REQUIRED_FIELD_VALUE)),
              validateLength(i18n._(L.PASSWORD_LENGTH_VALIDATOR_FAIL), 8),
              validateMatches(
                i18n._(L.PASSWORD_NON_NUMERICAL_VALIDATOR_FAIL),
                new RegExp(/[^0-9]/g)
              )
            )}
            component={FF_PasswordField}
            data-testid="activate-password1"
          />
          <Field
            block
            autoComplete="new-password"
            name="password_verify"
            placeholder={L.ACTIVATE_PASSWORD_VERIFY_PLACEHOLDER}
            label={L.ACTIVATE_PASSWORD_VERIFY_LABEL}
            validate={sequenceValidators(
              validateRequired(i18n._(L.REQUIRED_FIELD_VALUE)),
              (v: string, vs: ActivateFormData): string | undefined =>
                v !== vs?.password
                  ? i18n._(L.ACTIVATE_PASSWORD_VERIFY_MISMATCH)
                  : undefined
            )}
            component={FF_PasswordField}
            data-testid="activate-password2"
          />
        </div>
        <Button
          block
          type="submit"
          className={styles.cta}
          disabled={hasValidationErrors || (invalid && !dirtySinceLastSubmit)}
          data-testid="activate-cta"
          icon={ArrowRight}
          iconPlacement="after"
        >
          {L.ACTIVATE_CTA}
        </Button>
      </form>
    )}
  </Form>
);

export default ActivateForm;
